import { Grid, InputProps, MenuItem, Typography } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { Control, FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { powerUnit } from '../../../../util/formatters'
import { validators } from '../../../../validation/validators'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { FormGroup } from '../../../form/FormGroup'
import { FormValues } from '../form-values'
import { theme } from '../../../../theme'
import { ScheduleFragment } from '../../../../generated/graphql'

const centeredTextField: InputProps = {
  margin: 'dense',
  inputProps: { style: { textAlign: 'center' } },
}

type Props = {
  schedules: ScheduleFragment[]
  control: Control<FormValues>
  errors: FieldErrors<FormValues>
}

export function ElectricityConsumtionForm({
  schedules,
  control,
  errors,
}: Props): React.ReactElement {
  const [lightingShare, setLightingShare] = useState<number | undefined>(0)
  const [applianceShare, setApplianceShare] = useState<number | undefined>(0)
  const [ventilationShare, setVentilationShare] = useState<number | undefined>(
    0,
  )
  const [baseLoadShare, setBaseLoadShare] = useState<number | undefined>(0)

  const { t } = useTranslation()

  const calcPropertyElectricityShare = (share: number | undefined) =>
    share !== undefined ? 100 - share : undefined

  const parseNumber = async (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = event.target

    if (value.trim() === '') return 0

    const result = Number(value.replace(/\s/g, '').replace(',', '.'))

    if (await control.trigger(event.target.name))
      return Number.isNaN(result) ? undefined : result

    return undefined
  }

  useEffect(() => {
    setLightingShare(
      Number(
        control.defaultValuesRef.current.parameters?.electConsumption
          ?.occupantLightingElShare,
      ),
    )

    setApplianceShare(
      Number(
        control.defaultValuesRef.current.parameters?.electConsumption
          ?.occupantApplianceElShare,
      ),
    )

    setVentilationShare(
      Number(
        control.defaultValuesRef.current.parameters?.electConsumption
          ?.occupantVentilationElShare,
      ),
    )

    setBaseLoadShare(
      Number(
        control.defaultValuesRef.current.parameters?.electConsumption
          ?.occupantBaseLoadElShare,
      ),
    )
  }, []) // eslint-disable-line

  return (
    <FormGroup>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <ControlledTextField
            control={control}
            name="parameters.electConsumption.electricityBaseLoad"
            error={errors.parameters?.electConsumption?.electricityBaseLoad}
            label={t('energySystem.parameters.electConsumption.electBaseLoad')}
            rules={{ ...validators.required, ...validators.decimal }}
            InputProps={{
              endAdornment: <Typography>{powerUnit}</Typography>,
            }}
          />
        </Grid>

        <Grid item xs={8}>
          <ControlledTextField
            control={control}
            name="scheduleId"
            error={errors.scheduleId}
            label={t(
              'energySystem.parameters.electConsumption.baseLoadUsageProfile',
            )}
            rules={validators.required}
            select
          >
            {Object.values(schedules).map((schedule) => (
              <MenuItem key={schedule.id} value={schedule.id}>
                {schedule.name}
              </MenuItem>
            ))}
          </ControlledTextField>
        </Grid>
      </Grid>
      <Grid
        container
        item
        alignItems="flex-end"
        spacing={1}
        style={{ marginTop: theme.spacing(3), marginBottom: '5px' }}
      >
        <Grid item xs={4}>
          <Typography variant="body2">
            {t('energySystem.parameters.electConsumption.systemsLabel')}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" align="center">
            {t('energySystem.parameters.electConsumption.occupantElShareLabel')}{' '}
            [%]
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" align="center">
            {t('energySystem.parameters.electConsumption.propertyElShareLabel')}{' '}
            [%]
          </Typography>
        </Grid>
      </Grid>
      <Grid container item alignItems="center" spacing={1}>
        <Grid item xs={4}>
          <Typography>
            {t(
              'energySystem.parameters.electConsumption.occupantLightingShare',
            )}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <ControlledTextField
            control={control}
            name="parameters.electConsumption.occupantLightingElShare"
            error={errors.parameters?.electConsumption?.occupantLightingElShare}
            rules={{
              ...validators.required,
              ...validators.percent,
            }}
            onBlur={async (event) => setLightingShare(await parseNumber(event))}
            InputProps={centeredTextField}
          />
        </Grid>
        <Grid item xs={4}>
          <Typography align="center">
            {calcPropertyElectricityShare(lightingShare) ?? ''}
          </Typography>
        </Grid>
      </Grid>
      <Grid container item alignItems="center" spacing={1}>
        <Grid item xs={4}>
          <Typography>
            {t(
              'energySystem.parameters.electConsumption.occupantApplianceShare',
            )}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <ControlledTextField
            control={control}
            name="parameters.electConsumption.occupantApplianceElShare"
            error={
              errors.parameters?.electConsumption?.occupantApplianceElShare
            }
            rules={{
              ...validators.required,
              ...validators.percent,
            }}
            onBlur={async (event) =>
              setApplianceShare(await parseNumber(event))
            }
            InputProps={centeredTextField}
          />
        </Grid>
        <Grid item xs={4}>
          <Typography align="center">
            {calcPropertyElectricityShare(applianceShare) ?? ''}
          </Typography>
        </Grid>
      </Grid>
      <Grid container item alignItems="center" spacing={1}>
        <Grid item xs={4}>
          <Typography>
            {t(
              'energySystem.parameters.electConsumption.occupantVentilationShare',
            )}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <ControlledTextField
            control={control}
            name="parameters.electConsumption.occupantVentilationElShare"
            error={
              errors.parameters?.electConsumption?.occupantVentilationElShare
            }
            rules={{
              ...validators.required,
              ...validators.percent,
            }}
            onBlur={async (event) =>
              setVentilationShare(await parseNumber(event))
            }
            InputProps={centeredTextField}
          />
        </Grid>
        <Grid item xs={4}>
          <Typography align="center">
            {calcPropertyElectricityShare(ventilationShare) ?? ''}
          </Typography>
        </Grid>
      </Grid>
      <Grid container item alignItems="center" spacing={1}>
        <Grid item xs={4}>
          <Typography>
            {t(
              'energySystem.parameters.electConsumption.occupantBaseLoadShare',
            )}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <ControlledTextField
            control={control}
            name="parameters.electConsumption.occupantBaseLoadElShare"
            error={errors.parameters?.electConsumption?.occupantBaseLoadElShare}
            rules={{
              ...validators.required,
              ...validators.percent,
            }}
            onBlur={async (event) => setBaseLoadShare(await parseNumber(event))}
            InputProps={centeredTextField}
          />
        </Grid>
        <Grid item xs={4}>
          <Typography align="center">
            {calcPropertyElectricityShare(baseLoadShare) ?? ''}
          </Typography>
        </Grid>
      </Grid>
    </FormGroup>
  )
}
